import { CartQuery } from 'lib/api';
import React, { useRef } from 'react';
import type { FC } from 'react';
import { useCartStore } from 'store/cart';
import { tw } from 'twind';
import { Price } from '../Price';
import Description from '@/components/NewCart/Description';
import { ArrowRightIcon } from '@heroicons/react/outline';
import useItemPrice from '../../hooks/useItemPrice';
import useConfettiExplosion from '../../hooks/useConfettiExplosion';
import useOnScreen from '../../hooks/useOnScreen';
import useScreenHeight from '../../hooks/useScreenHeight';
import ImageGallery from '@/components/NewCart/ImageGallery/ImageGallery';

interface ListItemProps {
  item: CartQuery['Cart']['items'][number];
  onAddToCart: (productUrl: string) => void;
  applyDiscounts: boolean;
  usesDiscountModal: boolean;
}

export const ListItem: FC<ListItemProps> = ({ item, onAddToCart, applyDiscounts, usesDiscountModal }) => {
  const { theme, currency, selectedVariants } = useCartStore();
  const variant = selectedVariants[item.id];
  const price = useItemPrice(variant.price, currency, applyDiscounts);

  const goToProduct = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // @ts-ignore
    onAddToCart(item.productUrl ?? '');
  };

  const ref = useRef<HTMLDivElement | null>(null);
  const onScreen = useOnScreen(ref);
  const screenHeight = useScreenHeight();
  const confetti = useConfettiExplosion(applyDiscounts && usesDiscountModal && onScreen, {
    force: 0.6,
    particleCount: 40,
    duration: 2500,
    width: 200,
    height: screenHeight,
  });

  const urls = item.images.map(({ url }) => url) as string[];

  return (
    <div className={tw(`mb-[24px] shadow-${theme}-cart-box w-full max-w-3xl`)}>
      <ImageGallery urls={urls} />
      <Description name={item.name} description={item.description} />
      <div className={tw`px-[8px] py-[3px] rounded-b flex justify-between border border-${theme}-secondary text-${theme}-primary`}>
        <div className={tw`flex items-center justify-between w-full`}>
          <div ref={ref}>
            <Price price={price} />
            {confetti}
          </div>

          <button className={tw`bg-${theme} border-${theme} px-3 py-1 text-white flex items-center`} onClick={goToProduct}>
            <span className={tw`mr-2`}>Go to Product</span>
            <ArrowRightIcon className={tw`h-4 w-4 aria-hidden="true text-[#ffffff]`} />
          </button>
        </div>
      </div>
    </div>
  );
};
